import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GAME_STAGES, DEFAULT_ROOM } from "../../constants/gameStages";
import { SocketContext } from "../../context/SocketProvider";
import { useGameStage } from "../../hooks/useGameStage";
import { useSettings } from "../../context/SettingsContext";
import { useRoleBackground } from "../../hooks/useRoleBackground";
import { useTimer } from "../../context/TimerContext";
import { useLanguage } from "../../context/LanguageContext";

function DescribeImage() {
  const { socket } = useContext(SocketContext);
  const { t } = useLanguage();
  const { updateStage } = useGameStage();
  const [selectedImage, setSelectedImage] = useState(null); // {filename: "_.jpg", path: "./images/filename", url: "http:// + backendurl + path", index: 0-7}
  const { imageCount } = useSettings();
  const [description, setDescription] = useState(""); // Added this missing state
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { state } = useLocation();

  const { bgStyle, className } = useRoleBackground("pink");
  const { isExpired } = useTimer();

  useEffect(() => {
    const imageData = state?.selectedImage;
    if (!imageData) {
      console.error("No image data received, redirecting...");
      navigate("/pickingimage");
      return;
    }

    setSelectedImage(imageData);
    setLoading(false);
  }, [state, navigate]);

  const handleSubmit = () => {
    if (description.trim() || isExpired) {
      const allImages = state?.allImages || [];

      // Validate we got enough images
      if (allImages.length !== imageCount) {
        console.error(
          `Expected ${imageCount} images, but got: ${allImages.length}`
        );
        return;
      }

      if (!selectedImage && typeof selectedImage.index !== "number") {
        console.error("Selected image is invalid or missing index.");
        return;
      }

      socket.emit("submit_description", {
        room: DEFAULT_ROOM,
        images: allImages,
        description: description.trim() || t('error.noDescription', 'No description provided.'),
        image_describer: selectedImage.path,
      });
      socket.emit("pause_timer", { room: DEFAULT_ROOM });
      updateStage(GAME_STAGES.WAITING_FOR_GUESSER);
      navigate("/waitingforguesser");
    }
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setDescription(newDescription); // Update state
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex flex-col justify-center items-center ${className}`}
        style={bgStyle}
      >
        <div className="text-white text-2xl">{t('describer.loading', 'Loading images')}</div>
      </div>
    );
  }

  if (!selectedImage) {
    return (
      <div className="min-h-screen bg-aicom_blue flex flex-col justify-center items-center">
        <div className="text-white text-2xl mb-4">{t('describer.noImageSelected', 'No image selected')}</div>
        <button
          className="bg-aicom_pink text-white px-6 py-3 rounded font-bold"
          onClick={() => navigate("/pickingimagenormal")}
        >
          {t('describer.goBackToSelect', 'Go Back to Select Image')}
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-aicom_blue p-8 ${className}`} style={bgStyle}>
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-4xl font-bold mb-8">{t('describer.describeYourImage', 'Describe Your Image')}</h1>

        {/* Display the selected image */}
        <div className="bg-white p-4 rounded-lg shadow-lg mb-8 inline-block">
          <img
            src={selectedImage.url}
            alt="Selected image"
            className="w-80 h-80 object-cover rounded"
          />
        </div>

        {/* Description input area */}
        <div className="bg-white p-6 rounded-lg shadow-lg ">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('describer.whatDoYouSee', 'What do you see in this image?')}
          </h2>

          <textarea
            disabled={isExpired}
            value={description}
            onChange={handleDescriptionChange}
            placeholder={
              isExpired
                ? t('timer.timesUp', "Time's up!")
                : t('describer.prompt', "Describe what you see in the image but be careful that AiCOM-Chan can not understand!")
            }
            className="w-full h-40 p-4 border-2 border-gray-300 rounded-lg resize-none focus:border-aicom_pink focus:outline-none text-lg"
            maxLength={500}
          />

          <div className="text-right text-gray-500 text-sm mt-2">
            {description.length}/500 {t('describer.charactersCount', 'characters')}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col items-center gap-4 mt-8">
          {(description.trim() && selectedImage) || isExpired ? (
            <button
              onClick={handleSubmit}
              className={`px-8 py-4 rounded-lg font-bold text-xl transition-all ${
                isExpired
                  ? "bg-gray-500 cursor-pointer hover:bg-gray-600"
                  : "bg-aicom_pink hover:bg-opacity-90"
              }`}
            >
              {isExpired ? t('timer.submitHere', "Time's up – Submit Here") : t('button.submitDescription', "Submit Description")}
            </button>
          ) : (
            <div className="bg-gray-400 px-8 py-4 rounded-lg font-bold text-xl text-gray-600 cursor-not-allowed">
              {t('describer.enterDescription', 'Enter a Description to Continue')}
            </div>
          )}

          <button
            className="btn-pink-sm-transparent !w-[240px]"
            onClick={() =>
              navigate("/pickingimagenormal", {
                state: { shouldRefetch: false, allImages: state?.allImages }, // Pass current images if available
              })
            }
          >
            {t('button.backToImageSelection', 'Back to Image Selection')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DescribeImage;
